import { KeyboardEvent, ReactNode, useRef } from "react";

import { Colors, Portal } from "@blueprintjs/core";
import styled from "@emotion/styled";
import { useHotkeys } from "react-hotkeys-hook";
import { useNavigate } from "react-router";

import { Box } from "@/components/layout/flexbox";
import { breakpoints } from "@/components/layout/Media";
import { BackDrop } from "@/components/pieces/BackDrop";

import {
  DESKTOP_SERVICE_DETAIL_WIDTH,
  SERVICE_DETAILS_VERTICAL_MARGIN,
} from "./constants";

interface PortalContainerProps {
  active: boolean;
  backUrl: string;
  onCancel?: () => void;
  children: ReactNode;
  onScroll?: () => void;
  closeOnFocusEsc?: boolean;
  onClose?: () => void;
}

export function PortalContainer({
  active,
  backUrl,
  onCancel,
  children,
  onScroll,
  closeOnFocusEsc,
}: PortalContainerProps) {
  const ref = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  useHotkeys(
    "escape",
    (keyboardEvent) => {
      const escapingFromFocus = [document.body, ref.current].includes(
        keyboardEvent.target as HTMLElement,
      );
      if (
        closeOnFocusEsc &&
        escapingFromFocus &&
        !(keyboardEvent as unknown as KeyboardEvent).isPropagationStopped() &&
        onCancel
      ) {
        onCancel();
      }
    },
    {},
    [closeOnFocusEsc],
  );

  return (
    <>
      <Portal>
        <BackDrop visible={active} />
      </Portal>
      <Portal>
        <Box
          ref={ref}
          style={{
            position: "fixed",
            display: "block",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            overflowX: "hidden",
            overflowY: "auto",
            transform: active ? "translateY(0%)" : "translateY(120%)",
            visibility: active ? "visible" : "hidden",
            transition: active
              ? "transform 400ms"
              : "transform 400ms, visibility 0s 400ms",
          }}
          onScroll={onScroll}
          onClick={() => {
            navigate(backUrl, { replace: true });
            onCancel?.();
          }}
        >
          <StyledBox
            my={[0, SERVICE_DETAILS_VERTICAL_MARGIN]}
            mx="auto"
            style={{
              backgroundColor: Colors.WHITE,
              overflow: "hidden",
            }}
            width={DESKTOP_SERVICE_DETAIL_WIDTH}
            onClick={(e: React.MouseEvent<HTMLDivElement>) =>
              e.stopPropagation()
            }
          >
            {children}
          </StyledBox>
        </Box>
      </Portal>
    </>
  );
}

const StyledBox = styled(Box)`
  border-radius: 6px;

  @media screen and (max-width: ${breakpoints.sm - 1}px) {
    border-radius: 0px;
  }
`;
